// @ts-nocheck

import classNames from 'classnames';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import React from 'react';

import I13nLink from './I13nLink';

/**
 * Advanced BreadCrumb Component supporting substeps
 * Title and link of each step can change
 * Number of steps and substeps can't change after initialization
 */
class BreadCrumbs extends React.PureComponent {
  static defaultProps = {
    className: '',
    step: 0,
    linkData: [],
    validateClickable: (step, index) => step > index,
  };

  static propTypes = {
    /**
     * Array of link object, currently support { title, link, onClick }
     */
    linkData: PropTypes.arrayOf(
      PropTypes.shape({
        /**
         * Title to be displayed for the step
         */
        title: PropTypes.string,

        /**
         * Link to be used for clicks on the title
         */
        link: PropTypes.string,

        /**
         * [Optional] Callback for click on the title
         * @default R.identity
         */
        onClick: PropTypes.func,
      })
    ).isRequired,

    /**
     * Current step of the BreadCrumb
     * 0 indexed
     */
    step: PropTypes.number,

    /**
     * Callback to validate if BreadCrumb index is clickable
     */
    validateClickable: PropTypes.func,

    /**
     * Style classes
     */
    className: PropTypes.string,
  };

  constructor(props) {
    super(props);

    const { linkData } = props;
    const mainSteps = this._getMainStepsWithCompletionIndex(linkData);
    this.state = {
      mainSteps,
    };

    /**
     * These values never change after initialization
     *  - numberOfMainSteps
     *  - mainStepWidth
     *  - stepsWidth
     */
    this.numberOfMainSteps = mainSteps.length;
    this.mainStepWidth = this._calculateWidthPerStep(linkData);
    this.stepsWidth = this._convertStepsWidthToStyle(this._getStepWidthList(linkData));
  }

  componentWillReceiveProps(nextProps) {
    this.setState({
      mainSteps: this._getMainStepsWithCompletionIndex(nextProps.linkData),
    });
  }

  /**
   * Convert the step width to the style to be applied to status bar
   * and step dots
   * Every step dot will have a 6px added to it so that the dot is aligned
   * to the center of the step number
   * The completion dot will be at the end at 100%
   * @param  {Array.number} stepsWidth List of step width in percentage
   * @return {Array.string}            List of step width that will be applied
   */
  _convertStepsWidthToStyle(stepsWidth) {
    const stepsWidthStyle = stepsWidth.map((width) => {
      return `calc(${width}% + 6px)`;
    });
    stepsWidthStyle.push('100%');
    return stepsWidthStyle;
  }

  /**
   * Ignore the click on the title of current step
   * @param  {Object} e event object
   */
  ignoreTitleClick = (e) => {
    e.preventDefault();
  };

  /**
   * Get list of steps with titles(main steps) and their completion index
   * Completion index: the next step with a title(next main step)
   * First item in the list is always a main step
   * Any item with title not empty is a main step
   * Completion index for a main step is the index of the next main step
   * Completion index for the last step is lenght of linkData + 1
   * @param  {Array} linkData configuration for title and non-title
   * @return {Array}          list of configuration for title (main steps)
   * Returned array doesn't include substeps
   */
  _getMainStepsWithCompletionIndex(linkData) {
    return linkData.reduce((mainSteps, data, index) => {
      if (index === 0) {
        mainSteps.push({ ...data });
      } else if (data && data.title) {
        mainSteps[mainSteps.length - 1].completionIndex = index;
        mainSteps.push(Object.assign({}, data));
      }
      // biome-ignore lint/suspicious/noDoubleEquals: ignored using `--suppress`
      if (index == linkData.length - 1) {
        mainSteps[mainSteps.length - 1].completionIndex = index + 1;
      }
      return mainSteps;
    }, []);
  }

  // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
  _calculateWidthPerStep(linkData) {
    return 100 / this.numberOfMainSteps;
  }

  /**
   * width of each step in the progress bar
   * This will be used for style width
   * @param  {Array} linkData  configuration for title and non-title
   * @return {Array.number}    Array of width for each step
   */
  _getStepWidthList(linkData) {
    return this._getStepSubStepList(linkData).reduce((stepsWidth, data, index) => {
      return [...stepsWidth, ...this._calculateStepWidthWithStartingPoint(data, this.mainStepWidth, index)];
    }, []);
  }

  /**
   * Get list of main steps where each of them include
   * an integer indicating the number of sub steps (at least 1)
   * @param  {Array} linkData configuration for title and non-title
   * @return {Array.Array}   list (number of main steps) of list(substeps)
   * Example:
   * Input: [{title: 'blah'}, {}, {}, {title: 'blah2'}]
   * Output: [[0, 0, 0], [0]]
   */
  _getStepSubStepList(linkData) {
    return (
      linkData
        // biome-ignore lint/suspicious/noDoubleEquals: ignored using `--suppress`
        .map((data, index) => index == 0 || !!(data && data.title))
        .reduce((steps, hasTitle) => {
          hasTitle ? steps.push([0]) : steps[steps.length - 1].push(0);
          return steps;
        }, [])
    );
  }

  /**
   * calculate the width for each step for the progress bar
   * @param  {Array}  steps      list of substeps
   * @param  {number} maxWidth   maxWidth for the group of subsets
   * @param  {number} start      width where the steps must start
   * @return {Array.number}      list of width for the given substeps
   */
  _calculateStepWidthWithStartingPoint(steps, maxWidth, start) {
    const totalSteps = steps.length;
    // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
    return steps.map((step, index) => {
      return start * maxWidth + (maxWidth * index) / totalSteps;
    });
  }

  /**
   * Validate the given step
   * Change the given step to 0 if its less than 0
   * Change the given step to the last step if its greater than the last step
   * @param  {number} step current step (zero based)
   * @return {number}      valid current step (zero based)
   */
  _validateAndGetCurrentStep(step) {
    if (step < 0) {
      return 0;
    }
    const lengthOfData = this.props.linkData.length;
    if (step > lengthOfData) {
      return lengthOfData;
    }
    return step;
  }

  /**
   * Render progress bar
   * @param  {number} step current step (0 indexed)
   * @return {Object}      progress bar node
   */
  _renderStatusBar(step) {
    const currentStep = this._validateAndGetCurrentStep(step);
    return (
      <div className="breadcrumbs__status">
        <div className="breadcrumbs__status-bar" data-testid="status-bar" style={{ width: this.stepsWidth[currentStep] }} />
        {this.stepsWidth.map((item, index) => {
          return (
            <div
              key={index}
              className={`breadcrumbs__status-bar-dot ${currentStep >= index ? 'active' : ''}`}
              style={{ left: item }}
            />
          );
        })}
      </div>
    );
  }

  /**
   * Render title nodes
   * @param  {number} step              current step (0 indexed)
   * @param  {Array}  data              configuration for title
   * @param  {function} validateClickable validate if BreadCrumb index is clickable
   * @return {Array}                    Array of title nodes
   */
  _getTitleNodes(step, data, validateClickable) {
    return data.map((item, index) => {
      const { title, link, onClick = R.identity } = item;
      const isComplete = step >= item.completionIndex;
      const isClickable = validateClickable(step, index);

      return (
        <li
          className={classNames('breadcrumbs__item h_pos-r', {
            'breadcrumbs__item--complete': isComplete,
          })}
          data-step={index}
          key={index}
          data-testid={`item-${index}`}
          style={{ width: `${this.mainStepWidth}%` }}
        >
          <span className="breadcrumbs__step" data-testid="step">
            {isComplete ? <i className="breadcrumbs__check-icon material-icons h_c-success">check_circle</i> : index + 1}
          </span>
          <div className="breadcrumbs__title" data-testid="title">
            {title}
          </div>
          {link ? (
            <I13nLink
              className={classNames('h_stretched-box breadcrumbs__anchor', {
                'breadcrumbs__anchor--disabled': !isClickable,
              })}
              to={link}
              disabled={!isClickable}
              onClick={isClickable ? onClick : this.ignoreTitleClick}
            />
          ) : null}
        </li>
      );
    });
  }

  /**
   * Render title nodes
   * @param  {number} step              current step (0 indexed)
   * @param  {Array}  data              configuration for title
   * @param  {function} validateClickable validate if BreadCrumb index is clickable
   * @return {Object}                   title nodes
   */
  _renderTitle(step, data, validateClickable) {
    if (!data || !data.length) {
      return null;
    }
    return <ul className="breadcrumbs__items">{this._getTitleNodes(step, data, validateClickable)}</ul>;
  }

  render() {
    const { step, validateClickable, className } = this.props;

    return (
      <div className={`breadcrumbs ${className}`} data-testid="breadcrumbs">
        {this._renderTitle(step, this.state.mainSteps, validateClickable)}
        {this._renderStatusBar(step)}
      </div>
    );
  }
}

export default BreadCrumbs;
